// French UI copy for the console. Ported from solya-app's Settings.json (the strings
// that already exist there) and extended with the copy for the new CRUD actions the
// console adds (create/edit/delete custom roles, assign permissions). Code stays in
// English; every user-facing string lives here.

export const fr = {
  app: {
    title: "Console IAM",
    subtitle: "Rôles, permissions et attributions",
    nav: {
      roles: "Rôles",
      permissions: "Permissions",
      teams: "Équipes",
      users: "Utilisateurs"
    },
    orgLabel: "Organisation",
    orgPlaceholder: "Sélectionner une organisation…",
    loading: "Chargement…",
    authError: "Impossible d'authentifier la session de la console.",
    writesDisabledBanner:
      "Mode lecture seule — les écritures de la console sont désactivées jusqu'à la bascule (AUTH_ADMIN_WRITES_ENABLED). L'attribution et la modification sont indisponibles.",
    genericError: "Une erreur est survenue. Veuillez réessayer.",
    writesDisabledError:
      "Écriture refusée : la console est en lecture seule jusqu'à la bascule des écritures.",
    retry: "Réessayer",
    cancel: "Annuler",
    save: "Enregistrer",
    close: "Fermer",
    theme: {
      toLight: "Passer au thème clair",
      toDark: "Passer au thème sombre"
    },
    logout: "Se déconnecter"
  },
  roles: {
    title: "Rôles",
    description:
      "Rôles disponibles dans cette organisation. Chaque rôle regroupe un ensemble de permissions ; les rôles sont attribués directement aux utilisateurs ou hérités via les équipes.",
    systemBadge: "Système",
    customBadge: "Personnalisé",
    empty: "Aucun rôle disponible.",
    error: "Impossible de charger les rôles. Veuillez réessayer.",
    createButton: "Créer un rôle",
    columns: {
      displayName: "RÔLE",
      key: "CLÉ",
      permissionCount: "PERMISSIONS",
      type: "TYPE",
      actions: "ACTIONS"
    },
    create: {
      title: "Créer un rôle personnalisé",
      nameLabel: "Nom technique du rôle",
      namePlaceholder: "ex. shop_supervisor",
      displayNameLabel: "Nom affiché",
      descriptionLabel: "Description",
      submit: "Créer",
      errorGeneric: "Impossible de créer le rôle.",
      errorDuplicate: "Un rôle avec ce nom existe déjà dans cette organisation."
    },
    edit: {
      button: "Modifier",
      title: "Modifier le rôle",
      displayNameLabel: "Nom affiché",
      descriptionLabel: "Description",
      submit: "Enregistrer",
      systemReadonly: "Les rôles système sont gérés par le système et ne sont pas modifiables."
    },
    remove: {
      button: "Supprimer",
      dialogTitle: "Supprimer le rôle",
      dialogMessage:
        "Êtes-vous sûr de vouloir supprimer ce rôle ? Les utilisateurs et équipes perdront les permissions qu'il accorde.",
      confirm: "Supprimer",
      systemReadonly: "Les rôles système ne peuvent pas être supprimés."
    },
    permissions: {
      button: "Permissions",
      drawerTitle: "Permissions du rôle",
      assignTitle: "Attribuer des permissions",
      loading: "Chargement des permissions…",
      loadFailed: "Impossible de charger les permissions de ce rôle.",
      empty: "Aucune permission attribuée à ce rôle.",
      save: "Enregistrer les permissions",
      systemReadonly:
        "Les permissions des rôles système sont gérées par le système (lecture seule)."
    }
  },
  permissions: {
    title: "Permissions",
    description:
      "Catalogue de toutes les permissions utilisées par l'application, regroupées par périmètre puis par domaine.",
    empty: "Aucune permission définie.",
    error: "Impossible de charger les permissions.",
    count: (n: number) => `${n} permission${n > 1 ? "s" : ""}`,
    filter: {
      all: "Tous",
      areaLabel: "Filtrer par périmètre",
      searchPlaceholder: "Rechercher une permission…",
      empty: "Aucune permission ne correspond"
    },
    area: {
      pos: "Caisse (POS)",
      centrale: "Centrale",
      merch: "Merch",
      platform: "Plateforme"
    } as Record<string, string>,
    domain: {
      users: "Utilisateurs",
      teams: "Équipes",
      roles: "Rôles",
      org: "Organisation",
      organization: "Organisation",
      iam: "IAM",
      dataPlatform: "Plateforme de données",
      agentPlatform: "Plateforme agent",
      intelligenceLayer: "Couche d'intelligence",
      businessKnowledge: "Connaissances métier",
      inventoryPlans: "Plans d'inventaire",
      signals: "Signaux",
      analytics: "Analyses",
      shops: "Magasins"
    } as Record<string, string>
  },
  userRoles: {
    title: "Rôles utilisateur",
    description:
      "Consultez et gérez les rôles d'un utilisateur au sein de l'organisation sélectionnée.",
    userLabel: "Utilisateur",
    userPlaceholder: "Sélectionner un utilisateur…",
    noUsers: "Aucun utilisateur dans cette organisation.",
    assignedTitle: "Rôles attribués",
    availableTitle: "Rôles disponibles",
    empty: "Aucun rôle attribué à cet utilisateur dans cette organisation.",
    assign: "Attribuer",
    remove: "Retirer",
    error: "Impossible de charger les rôles de l'utilisateur.",
    assignError: "Impossible d'attribuer le rôle.",
    removeError: "Impossible de retirer le rôle.",
    selectUserFirst: "Sélectionnez un utilisateur pour voir ses rôles.",
    // Assignment flow: a searchable list of the roles this user does NOT have.
    assignedCount: (n: number) => `${n} rôle${n > 1 ? "s" : ""}`,
    /** Per-item labels: they must name the role, not just the action — a list of
     *  chips/rows yields many controls that would otherwise be indistinguishable. */
    assignLabel: (role: string) => `Attribuer le rôle ${role}`,
    removeLabel: (role: string) => `Retirer le rôle ${role}`,
    assignSearchLabel: "Rechercher un rôle à attribuer",
    assignSearchPlaceholder: "Rechercher un rôle…",
    assignHint: "Sélectionnez un rôle pour l'attribuer immédiatement.",
    allAssigned: "Tous les rôles de cette organisation sont déjà attribués.",
    noMatch: "Aucun rôle ne correspond à la recherche.",
    assignSuccess: (role: string) => `Rôle « ${role} » attribué.`,
    removeSuccess: (role: string) => `Rôle « ${role} » retiré.`
  },
  teams: {
    title: "Équipes",
    description:
      "Organisez les utilisateurs en équipes pour faciliter la gestion des permissions. Les rôles attribués à une équipe sont hérités par tous ses membres.",
    empty: "Aucune équipe dans cette organisation.",
    error: "Impossible de charger les équipes. Veuillez réessayer.",
    noOrg: "Sélectionnez une organisation pour voir ses équipes.",
    createButton: "Créer une équipe",
    columns: {
      name: "ÉQUIPE",
      members: "MEMBRES",
      roles: "RÔLES",
      actions: "ACTIONS"
    },
    memberCount: (n: number) => `${n} membre${n > 1 ? "s" : ""}`,
    roleCount: (n: number) => `${n} rôle${n > 1 ? "s" : ""}`,
    create: {
      title: "Créer une équipe",
      nameLabel: "Nom de l'équipe",
      namePlaceholder: "ex. Équipe caisse",
      descriptionLabel: "Description",
      submit: "Créer",
      errorGeneric: "Impossible de créer l'équipe.",
      errorDuplicate: "Une équipe avec ce nom existe déjà dans cette organisation."
    },
    edit: {
      button: "Modifier",
      title: "Modifier l'équipe",
      submit: "Enregistrer"
    },
    remove: {
      button: "Supprimer",
      dialogTitle: "Supprimer l'équipe",
      dialogMessage:
        "Êtes-vous sûr de vouloir supprimer cette équipe ? Ses membres perdront les rôles qu'elle accorde.",
      confirm: "Supprimer",
      success: "Équipe supprimée."
    },
    manage: {
      button: "Gérer",
      drawerTitle: "Composition de l'équipe",
      membersTitle: "Membres",
      membersHint: "Sélectionnez un utilisateur pour l'ajouter à l'équipe.",
      membersEmpty: "Aucun membre dans cette équipe.",
      allMembers: "Tous les utilisateurs de l'organisation sont déjà membres.",
      memberSearchLabel: "Rechercher un utilisateur à ajouter",
      memberSearchPlaceholder: "Rechercher par nom ou e-mail…",
      addMemberLabel: (name: string) => `Ajouter ${name} à l'équipe`,
      removeMemberLabel: (name: string) => `Retirer ${name} de l'équipe`,
      addMemberSuccess: (name: string) => `${name} ajouté à l'équipe.`,
      removeMemberSuccess: (name: string) => `${name} retiré de l'équipe.`,
      noMemberMatch: "Aucun utilisateur ne correspond à la recherche.",
      rolesTitle: "Rôles de l'équipe",
      rolesHint: "Les rôles ci-dessous sont hérités par tous les membres.",
      rolesEmpty: "Aucun rôle attribué à cette équipe.",
      allRoles: "Tous les rôles de l'organisation sont déjà attribués.",
      roleSearchLabel: "Rechercher un rôle à attribuer",
      roleSearchPlaceholder: "Rechercher un rôle…",
      assignRoleLabel: (role: string) => `Attribuer le rôle ${role} à l'équipe`,
      removeRoleLabel: (role: string) => `Retirer le rôle ${role} de l'équipe`,
      assignRoleSuccess: (role: string) => `Rôle « ${role} » attribué à l'équipe.`,
      removeRoleSuccess: (role: string) => `Rôle « ${role} » retiré de l'équipe.`,
      noRoleMatch: "Aucun rôle ne correspond à la recherche.",
      loadFailed: "Impossible de charger l'équipe.",
      loading: "Chargement de l'équipe…"
    }
  },
  users: {
    title: "Utilisateurs",
    description:
      "Utilisateurs de l'organisation sélectionnée. Cliquez sur un utilisateur pour consulter et gérer ses rôles.",
    searchLabel: "Rechercher un utilisateur",
    searchPlaceholder: "Rechercher par nom ou e-mail…",
    columns: {
      name: "NOM",
      email: "E-MAIL",
      org: "ORGANISATION",
      roles: "RÔLES",
      teams: "ÉQUIPES",
      actions: "ACTIONS"
    },
    rolesEmpty: "Aucun rôle",
    rolesMore: (n: number) => `+${n}`,
    teamsEmpty: "Aucune équipe",
    teamsMore: (n: number) => `+${n}`,
    empty: "Aucun utilisateur dans cette organisation.",
    noMatch: "Aucun utilisateur ne correspond à la recherche.",
    error: "Impossible de charger les utilisateurs. Veuillez réessayer.",
    noOrg: "Sélectionnez une organisation pour voir ses utilisateurs.",
    manage: "Gérer les rôles",
    resultCount: (total: number) => `${total} utilisateur${total > 1 ? "s" : ""}`,
    pagination: {
      previous: "Précédent",
      next: "Suivant",
      page: (current: number, total: number) => `Page ${current} sur ${total}`
    },
    detail: {
      back: "← Utilisateurs",
      subtitle: "Rôles et permissions dans l'organisation sélectionnée.",
      orgContext: (org: string) => `Organisation : ${org}`,
      notFoundTitle: "Utilisateur introuvable",
      notFound:
        "Cet utilisateur n'existe pas dans l'organisation sélectionnée. Il a peut-être changé d'organisation.",
      permissionsTitle: "Permissions effectives",
      permissionsHint:
        "Union des permissions accordées par les rôles attribués dans cette organisation.",
      permissionsEmpty: "Aucune permission accordée par les rôles attribués.",
      teamsTitle: "Équipes",
      teamsHint: "Équipes de cette organisation auxquelles l'utilisateur appartient.",
      teamsEmpty: "Cet utilisateur n'appartient à aucune équipe dans cette organisation.",
      teamsError: "Impossible de charger les équipes de l'utilisateur."
    }
  },
  // Shop assignment (the "Magasins" card on the user- and team-detail surfaces).
  shops: {
    title: "Magasins",
    userHint: "Magasins auxquels cet utilisateur a accès dans cette organisation.",
    teamHint: "Magasins accessibles à tous les membres de cette équipe.",
    loading: "Chargement des magasins…",
    error: "Impossible de charger les magasins attribués.",
    catalogError:
      "Impossible de charger le catalogue des magasins de l'organisation. Réessayez plus tard.",
    catalogEmpty: "Aucun magasin disponible dans cette organisation.",
    count: (n: number) => `${n} magasin${n > 1 ? "s" : ""}`,
    save: "Enregistrer les magasins",
    saved: "Magasins mis à jour.",
    saveError: "Impossible d'enregistrer les magasins.",
    /** Names the granting role — the assignment is inert when a VIEW_ALL role applies. */
    viewAllBanner: (role: string) =>
      `Accès à tous les magasins de l'organisation via le rôle « ${role} ». L'attribution par magasin est sans effet.`,
    toggleLabel: (name: string) => `Basculer le magasin ${name}`
  }
} as const
